import config from 'constants/config'
import { min, nonNullable } from 'lib/array/arrayUtils'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { getAllOrderItems, isBedbankOrderItem, isExperienceOrderItem, isFlightOrderItem, isHotelOrderItem, isLuxPlusSubscriptionOrderItem, isStandaloneFlightsOrder } from './orderUtils'
import { ITEM_STATUS_CANCELLED } from 'constants/cart'
import { addHours, endOfDay } from 'lib/datetime/dateUtils'
import { isStandaloneExperience } from 'lib/experiences/orderExperienceClassification'
import { isBeyondCfmrLeadTime, isCoverGeniusEnabled } from 'lib/insurance/insuranceHelpers'
import verifyItemFollowsRegionalRules from 'lib/insurance/verifyItemFollowsRegionalRules'
import { isGroupPaymentParticipantOrder } from 'lib/payment/groupPaymentDetailsUtils'
import { getInsuranceDatesFromOrder } from './orderInsuranceUtils'

function getInsuranceBookingProtectionDestinationCountryCodesFromOrder(
  order: App.Order,
  flightDetails: Record<string, App.FlightOrderItemDetails>,
  offers: Record<string, App.Offer> = {},
  experiencesOffers: Record<string, App.ExperienceOffer> = {},
): Array<string> {
  if (order.items.length) {
    return nonNullable(order.items.map(item =>
      item.offer.property?.geoData?.countryCode ||
      // In some cases, the geodata snapshot is not available at the order level
      // Therefore, we retrieve it directly from the offer that was fetched during checkout loading
      offers[item.offerId]?.property?.geoData.country))
  } else if (order.bedbankItems.length) {
    return order.bedbankItems.map(item => item.offer.property.address.countryCode)
  } else if (order.experienceItems.length) {
    const experienceIds = Object.keys(experiencesOffers)

    const destinationCountryCodes = experienceIds.flatMap((experienceId) => {
      const offer: App.ExperienceOffer | undefined = experiencesOffers[experienceId]
      if (!offer) return

      return offer.redemptionLocations.map(location => location.countryCode)
    })

    return nonNullable(destinationCountryCodes)
  } else if (order.flightItems.length) {
    if (!Object.keys(flightDetails).length) return []

    const flightItemsIds = order.flightItems.map(item => item.id)

    const destinationCountryCodes = flightItemsIds.reduce<Array<string>>((acc, curr) => {
      const currentFlightDetails: App.FlightOrderItemDetails | undefined = flightDetails[curr]
      const countryCodes = currentFlightDetails?.bookingJourney.legs.map(leg => leg.arrivalCountryCode) || []
      return [...acc, ...countryCodes]
    }, [])

    return destinationCountryCodes
  }

  return []
}

function getEarliestHotelCheckInDateFromOrder(order: App.Order): Date | undefined {
  const hotelCheckInDates: Array<Date> = []

  // Hotel items (LE hotel-style order items with reservations)
  for (const item of order.items) {
    if (item.status !== ITEM_STATUS_CANCELLED && isHotelOrderItem(item) && item.reservation?.startDate) {
      hotelCheckInDates.push(new Date(item.reservation.startDate))
    }
  }

  // Bedbank items (Moment dates)
  for (const item of order.bedbankItems) {
    if (item.status !== ITEM_STATUS_CANCELLED && item.checkIn) {
      hotelCheckInDates.push(item.checkIn.toDate())
    }
  }

  if (hotelCheckInDates.length === 0) return undefined

  return min(hotelCheckInDates, date => date.getTime())
}

export function getRemainingSecondsToPostPurchaseInsuranceBookingProtection(order: App.Order) {
  const orderCreatedAt = new Date(order.createdAt)
  const nextDayAfterPurchase = addHours(orderCreatedAt, 24)
  const deadline = endOfDay(nextDayAfterPurchase)
  const diffInMilliseconds = deadline.getTime() - new Date().getTime()
  return Math.floor(diffInMilliseconds / 1000)
}

export function canAddInsuranceBookingProtectionToOrder(
  order: App.Order,
  regionCode: string,
  flightDetails: Record<string, App.FlightOrderItemDetails>,
  offers: Record<string, App.Offer> = {},
  experiencesOffers: Record<string, App.ExperienceOffer> = {},
  experienceBookingDetails: Record<string, App.ExperienceBookingDetails> = {},
) {
  if (isStandaloneFlightsOrder(order)) return false

  // Experience booking details are created asynchronously and are not available immediately after order creation
  // We need these details to extract traveller information
  // Without them, we cannot properly request a quote
  // --------
  // Multi Cart Experiences - "isStandaloneExperience" checks if all items are from same offer, dates, etc,
  // so we should be safe to assume the first item here
  const experienceItem = order.experienceItems.length ? order.experienceItems[0] : undefined
  if (experienceItem && isStandaloneExperience(experienceItem, order)) {
    const isExperienceDetailsLoaded = !!Object.values(experienceBookingDetails).filter(booking => booking.orderId === order.id).length
    if (!isExperienceDetailsLoaded) return false
  }

  // Period validation
  const secondsRemaining = getRemainingSecondsToPostPurchaseInsuranceBookingProtection(order)
  if (secondsRemaining <= 0) return false

  // Destination countries validation
  const earliestHotelCheckInDate = getEarliestHotelCheckInDateFromOrder(order)
  const destinationCountryCodes = getInsuranceBookingProtectionDestinationCountryCodesFromOrder(order, flightDetails, offers, experiencesOffers)
  const areEnabledDestinationCountries =
    destinationCountryCodes.length && destinationCountryCodes.every(countryCode => {
      return verifyItemFollowsRegionalRules(countryCode, earliestHotelCheckInDate)
    })
  if (!areEnabledDestinationCountries) return false

  // Validate if has already been purchased
  const isAlreadyPurchased = order.insuranceItems.some(i => i.status !== ITEM_STATUS_CANCELLED)
  if (isAlreadyPurchased) return false

  // On Group Payment orders only the lead can add insurance
  if (isGroupPaymentParticipantOrder(order)) return false

  // Region validation
  const isEnabledRegion = isEnabledForFeature(config.INSURANCE_BOOKING_PROTECTION_ENABLED_REGIONS, regionCode)
  if (!isEnabledRegion) return false

  // Lead time validation: long-lead trips are offered the XCover upsell instead wherever the region sells it
  if (isCoverGeniusEnabled(regionCode) && isBeyondCfmrLeadTime(getInsuranceDatesFromOrder(order)?.startDate)) return false

  // Products validation
  const orderItems = getAllOrderItems(order)
  const areEnabledProducts = orderItems.every(item =>
    isHotelOrderItem(item) ||
    isBedbankOrderItem(item) ||
    isExperienceOrderItem(item) ||
    isFlightOrderItem(item) ||
    isLuxPlusSubscriptionOrderItem(item),
  )
  if (!areEnabledProducts) return false

  return true
}
